/**
 * Exmaple implementation:
 *
 *   <DocumentsSearch onSubmit={onSubmit} />
 *
 * See also hellospa/components/signature-requests/search.tsx.
 */

import React from 'react';
import { useIntl, defineMessages, FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import { SearchBar, SearchBarPropTypes } from 'hellospa/foundation/ui/search-bar';
import { UIIcon } from 'dig-components/icons';
import { ChevronDownLine, CloseLine } from 'dig-components/icons/src';
import { Menu } from 'dig-components/menu';
import { IconButton, IconButtonProps } from 'dig-components/buttons';
import { trackHeapCustomEvent } from 'common/utils/heap';

import AdvancedSearchModal from './advanced-search-modal';

import styles from './search.scss';
import { TextInputRefObject } from 'dig-components/text_fields';

enum SearchMode {
  Basic,
  Advanced,
}

const messages = defineMessages({
  searchPlaceholder: {
    id: '',
    description: 'Placeholder text for a search field.',
    defaultMessage: 'Search by name, email, or keyword',
  },
});

function CloseButton(props: Omit<IconButtonProps, 'variant'>) {
  return (
    <IconButton
      data-qa-ref="search-bar-close"
      type="button"
      className={styles.closeButton}
      variant="transparent"
      {...props}
    >
      <UIIcon src={CloseLine} />
    </IconButton>
  );
}

export type DocumentsSearchPropTypes = SearchBarPropTypes & {
  currentValue: string,
  requestShowAdvancedSearchModal: (show?: boolean) => void,
  showAdvancedSearchModal?: boolean,
  allowAdvancedSearch?: boolean,
  isOpen?: boolean,
  onFocus?: React.BaseHTMLAttributes<HTMLInputElement>['onFocus'],
  onSearch?: (query: string) => void,
  onClosePressed?: Function,
  hasTeamRequestTab?: boolean,
  disabled?: boolean,
}

function DocumentsSearch({
  currentValue,
  onChange,
  onSearch,
  onClosePressed,
  requestShowAdvancedSearchModal,
  isOpen = false,
  className = '',
  allowAdvancedSearch = false,
  showAdvancedSearchModal = false,
  hasTeamRequestTab = false,
  disabled = false,
  children,

  // These will be applied to the input field
  onFocus,

  // add'l props will be applied to the root <form> element
  ...props
}: DocumentsSearchPropTypes) {
  const intl = useIntl();
  const [internalValue, setInternvalValue] = React.useState(currentValue);
  const fieldRef = React.useRef<TextInputRefObject>(null);

  const onChangeProxy = React.useCallback((evt) => {
    setInternvalValue(evt.currentTarget.value);

    if (onChange) {
      onChange(evt);
    }
  }, [onChange]);

  const onClosePressedProxy = React.useCallback((evt) => {
    evt.stopPropagation(); // don't allow this to be considered a focus event

    if (internalValue !== '') {
      setInternvalValue('');

      if (onSearch) {
        onSearch(''); // Request query reset
      }
    }

    if (onClosePressed) {
      onClosePressed();
    }
  }, [internalValue, onClosePressed, onSearch]);

  const onSearchProxy = React.useCallback((evt) => {
    evt.preventDefault();

    trackHeapCustomEvent('docs_redesign_perform_standard_search');

    if (onSearch) {
      onSearch(internalValue);
    }
  }, [internalValue, onSearch]);

  const onAdvancedSearch = React.useCallback((q) => {
    setInternvalValue(q);

    requestShowAdvancedSearchModal(false);

    if (onSearch) {
      onSearch(q);
    }
  }, [requestShowAdvancedSearchModal, onSearch]);

  const onSelectSearchType = React.useCallback((value: SearchMode) => {
    switch (value) {
      case SearchMode.Advanced:
        requestShowAdvancedSearchModal(true);
        break;
      case SearchMode.Basic:
      default:
        requestShowAdvancedSearchModal(false);

        if (onSearch) {
          onSearch(internalValue);
        }
    }
  }, [onSearch, internalValue, requestShowAdvancedSearchModal]);

  const onRequestCloseAdvancedSearchModal = React.useCallback(() => {
    requestShowAdvancedSearchModal(false);
  }, [requestShowAdvancedSearchModal]);

  const onSearchFieldClick = React.useCallback(() => {
    if (!isOpen && fieldRef.current) {
      fieldRef.current.focus();
    }
  }, [isOpen]);

  React.useEffect(
    () => {
      if (currentValue !== internalValue) {
        setInternvalValue(currentValue);
      }
    },
    // We only want the cached value of internalValue, and
    // we don't want to respond if it changes, therefore...
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      currentValue,
    ],
  );

  return (
    <SearchBar
      onSubmit={onSearchProxy}
      className={classnames(className, styles.search, {
        [styles.isOpen]: isOpen,
      })}
      {...props}
    >
      {children}
      <SearchBar.Field
        disabled={disabled}
        ref={fieldRef}
        className={styles.field}
        placeholder={intl.formatMessage(messages.searchPlaceholder)}
        onClick={onSearchFieldClick}
        onChange={onChangeProxy}
        onFocus={onFocus}
        value={internalValue}
        autoComplete="off" />
      {isOpen && internalValue.length > 0 && (
        <div className={styles.searchButtons}>
          <SearchBar.Submit className={styles.searchButton} />
          {allowAdvancedSearch && (
            <Menu.Wrapper onSelection={onSelectSearchType}>
              {({ getTriggerProps, getContentProps }) => (
                <>
                  <IconButton
                    type="button"
                    variant="transparent"
                    className={styles.searchDropdown}
                    {...getTriggerProps()}
                  >
                    <UIIcon src={ChevronDownLine} aria-label="More options" />
                  </IconButton>
                  <Menu.Content {...getContentProps()}>
                    <Menu.Segment>
                      <Menu.ActionItem
                        value={SearchMode.Basic}
                        data-qa-ref="search-dropdown-basic-search"
                      >
                        <FormattedMessage
                          id=""
                          description="Option in a drop down allowing the user to select how they would like to search"
                          defaultMessage="Search" />
                      </Menu.ActionItem>
                      <Menu.ActionItem
                        value={SearchMode.Advanced}
                        data-qa-ref="search-dropdown-advanced-search"
                      >
                        <FormattedMessage
                          id=""
                          description="Option in a drop down allowing the user to select how they would like to search"
                          defaultMessage="Advanced search" />
                      </Menu.ActionItem>
                    </Menu.Segment>
                  </Menu.Content>
                </>
              )}
            </Menu.Wrapper>
          )}
        </div>
      )}
      {isOpen && <CloseButton onClick={onClosePressedProxy} />}
      {showAdvancedSearchModal && (
        <AdvancedSearchModal
          open
          hasTeamRequestTab={hasTeamRequestTab}
          onRequestClose={onRequestCloseAdvancedSearchModal}
          onSearch={onAdvancedSearch}
          defaultQuery={internalValue} />
      )}
    </SearchBar>
  );
}

export default DocumentsSearch;
